import React, { useEffect, useState } from 'react';
import {
  ShoppingCart,
  Scale,
  ChefHat,
  Leaf,
  Star,
  AlertCircle,
  ChevronRight,
  Wine,
  Maximize2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import { formatPrice, formatPricePerKg } from '@/lib/formatPrice';
import { resolveImageUrl } from '@/lib/utils';
import { APP_BASE } from '@/lib/api';
import { DishImagePreviewDialog } from '@/components/DishImagePreviewDialog';
import { buildCategoryGroups } from '@/lib/menuCategoryGroups';

const badgeStyles = {
  consigliato: 'gradient-instagram text-primary-foreground border-0',
  vegetariano: 'bg-success/20 text-success border-success/30',
  'senza-glutine': 'bg-warning/20 text-warning-foreground border-warning-foreground/30',
  novita: 'bg-accent/20 text-accent-foreground border-accent/30',
};

const badgeLabels = {
  consigliato: 'Consigliato',
  vegetariano: 'Vegetariano',
  'senza-glutine': 'Senza Glutine',
  novita: 'Novita',
};

const DishCard = ({ dish, categoryName, showPricesOnHome }) => {
  const [grams, setGrams] = useState(300);
  const [senzaLattosio, setSenzaLattosio] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const { addItem } = useCart();

  const canShowPrice =
    showPricesOnHome &&
    dish.show_price !== false &&
    dish.price_value != null;

  const price300g = dish.price_value;
  const pricePerKg = price300g ? (price300g / 0.3) : null;
  const isFixedPrice = dish.price_type === 'fixed';

  const hasLactoseOption =
    dish.has_lactose_option === 1 ||
    dish.has_lactose_option === true ||
    dish.has_lactose_option === '1';

  const calculatedPrice = isFixedPrice
    ? price300g
    : (pricePerKg ? (grams / 1000) * pricePerKg : null);

  const handleAddToCart = () => {
    if (calculatedPrice == null) return;

    const dishWithPrice = {
      ...dish,
      category: categoryName,
      price300g,
      pricePerKg,
      calculatedPrice,
      isFixedPrice,
    };

    const itemOptions = {
      senza_lattosio: hasLactoseOption ? senzaLattosio : false,
    };

    if (isFixedPrice) {
      addItem(dishWithPrice, 1, true, itemOptions);
    } else {
      addItem(dishWithPrice, grams, false, itemOptions);
    }

    const priceLabel = isFixedPrice
      ? formatPrice(calculatedPrice)
      : `${grams}g - ${formatPrice(calculatedPrice)}`;

    const lattosioLabel = hasLactoseOption
      ? ` - Senza lattosio: ${senzaLattosio ? 'Si' : 'No'}`
      : '';

    toast.success(`${dish.name} (${priceLabel})${lattosioLabel} aggiunto al carrello!`, {
      duration: 2000,
    });
  };

  const categoryColors = {
    'primi piatti': 'from-instagram-red/10 to-instagram-orange/10',
    'secondi piatti': 'from-instagram-orange/10 to-instagram-yellow/5',
    contorni: 'from-success/10 to-success/5',
    antipasti: 'from-instagram-purple/10 to-instagram-red/5',
    dolci: 'from-pink-500/10 to-pink-300/5',
  };

  const normalizedCategory = categoryName?.toLowerCase() || 'altro';
  const bgColor = categoryColors[normalizedCategory] || 'from-muted/30 to-muted/10';
  const hasImage = !!dish.image_url;
  const imageUrl = resolveImageUrl(dish.image_url);
  const fixedHeightClass = isFixedPrice ? 'min-h-[390px] sm:min-h-[430px] md:min-h-[450px]' : '';
  const canPreviewImage = hasImage && !!imageUrl;
  const canPreviewFromCard =
    canPreviewImage &&
    !(typeof window !== 'undefined' && window.matchMedia('(hover: none), (pointer: coarse)').matches);
  const openPreview = () => setIsPreviewOpen(true);

  const handlePreviewOpen = (event) => {
    if (!canPreviewFromCard) return;

    const interactiveElement = event.target.closest(
      'button, a, input, textarea, select, label, [role="button"], [role="slider"], [data-no-preview="true"]'
    );

    if (interactiveElement && interactiveElement !== event.currentTarget) return;
    openPreview();
  };

  const handlePreviewKeyDown = (event) => {
    if (!canPreviewImage || event.target !== event.currentTarget) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openPreview();
    }
  };

  return (
    <>
      <Card
        className={`relative h-full overflow-hidden rounded-xl border border-border/50 transition-all duration-300 hover:border-instagram-purple/30 hover:shadow-medium ${
          !hasImage ? `bg-gradient-to-br ${bgColor}` : ''
        } ${fixedHeightClass} ${canPreviewFromCard ? 'cursor-zoom-in' : ''}`}
        onClick={handlePreviewOpen}
        onKeyDown={handlePreviewKeyDown}
        role={canPreviewFromCard ? 'button' : undefined}
        tabIndex={canPreviewFromCard ? 0 : undefined}
        aria-label={canPreviewFromCard ? `Apri immagine di ${dish.name}` : undefined}
      >
        {hasImage && imageUrl ? (
          <>
            <img
              src={imageUrl}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover object-center"
              loading="lazy"
              onError={(event) => {
                event.target.style.display = 'none';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20" />
          </>
        ) : null}

        {canPreviewImage ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              openPreview();
            }}
            className="absolute right-3 top-3 z-20 inline-flex items-center gap-2 rounded-full border border-white/30 bg-black/45 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-white backdrop-blur-md transition hover:bg-black/60"
            aria-label={`Ingrandisci immagine di ${dish.name}`}
          >
            <Maximize2 className="h-3.5 w-3.5" />
            <span>Ingrandisci</span>
          </button>
        ) : null}

        <div className={`relative z-10 flex h-full flex-col gap-4 p-4 sm:p-5 ${hasImage ? 'text-white' : ''}`}>
          <div className="flex-1">
            <div className="mb-2 flex items-start justify-between gap-2">
              <h3 className={`text-base font-semibold leading-tight sm:text-lg ${hasImage ? 'text-white drop-shadow-md' : 'text-foreground'}`}>
                {dish.name}
              </h3>

              {dish.badges && dish.badges.includes('consigliato') ? (
                <Star
                  className={`h-5 w-5 flex-shrink-0 ${hasImage ? 'text-yellow-400' : 'text-instagram-orange'}`}
                  fill="currentColor"
                />
              ) : null}
            </div>

            {dish.description ? (
              <p className={`text-sm line-clamp-2 ${hasImage ? 'text-white/90 drop-shadow' : 'text-muted-foreground'}`}>
                {dish.description}
              </p>
            ) : null}

            {dish.badges && dish.badges.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {dish.badges.map((badge) => (
                  <Badge
                    key={badge}
                    variant="outline"
                    className={`px-2 py-0.5 text-[10px] ${
                      hasImage
                        ? 'border-white/30 bg-white/20 text-white backdrop-blur-sm'
                        : badgeStyles[badge] || ''
                    }`}
                  >
                    {badgeLabels[badge] || badge}
                  </Badge>
                ))}
              </div>
            ) : null}
          </div>

          {canShowPrice && price300g != null ? (
            <div className={`space-y-3 border-t pt-2 ${hasImage ? 'border-white/20' : 'border-border/50'}`}>
              {isFixedPrice ? (
                <>
                  <div className="flex items-center justify-between">
                    <span className={hasImage ? 'text-white/80' : 'text-muted-foreground'}>Prezzo:</span>
                    <span className={`text-2xl font-bold ${hasImage ? 'text-white drop-shadow-lg' : 'gradient-instagram-text'}`}>
                      {formatPrice(price300g)}
                    </span>
                  </div>

                  {hasLactoseOption ? (
                    <div className={`rounded-lg border p-3 ${hasImage ? 'border-white/25 bg-transparent shadow-none' : 'border-border/50 bg-background/70 shadow-lg'}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className={`text-sm font-medium ${hasImage ? 'text-white' : 'text-foreground'}`}>
                            Senza lattosio
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() => setSenzaLattosio((prev) => !prev)}
                          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                            senzaLattosio ? 'bg-green-500' : hasImage ? 'bg-white/30' : 'bg-muted'
                          }`}
                          aria-pressed={senzaLattosio}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                              senzaLattosio ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  ) : null}

                  <Button
                    onClick={handleAddToCart}
                    className={`w-full ${hasImage ? 'bg-white text-gray-900 hover:bg-white/90' : 'gradient-instagram text-primary-foreground hover:opacity-90'} shadow-instagram`}
                    data-testid={`add-to-cart-${dish.id}`}
                  >
                    <ShoppingCart className="mr-2 h-4 w-4" />
                    Aggiungi al Carrello
                  </Button>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between text-sm">
                    <span className={hasImage ? 'text-white/80' : 'text-muted-foreground'}>Prezzo 300g:</span>
                    <span className={`font-medium ${hasImage ? 'text-white' : 'text-foreground'}`}>{formatPrice(price300g)}</span>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className={hasImage ? 'text-white/80' : 'text-muted-foreground'}>Prezzo al kg:</span>
                    <span className={`font-medium ${hasImage ? 'text-white' : 'text-foreground'}`}>{formatPricePerKg(pricePerKg)}</span>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Scale className={`h-4 w-4 ${hasImage ? 'text-white' : 'text-instagram-purple'}`} />
                        <span className={`text-sm font-medium ${hasImage ? 'text-white' : 'text-foreground'}`}>
                          Quantita:
                        </span>
                      </div>
                      <span className={`text-sm font-bold ${hasImage ? 'text-white' : 'gradient-instagram-text'}`}>
                        {grams}g
                      </span>
                    </div>

                    <Slider
                      value={[grams]}
                      onValueChange={(value) => setGrams(value[0])}
                      min={100}
                      max={1000}
                      step={50}
                      className="w-full"
                    />

                    <div className={`flex justify-between text-xs ${hasImage ? 'text-white/70' : 'text-muted-foreground'}`}>
                      <span>100g</span>
                      <span>500g</span>
                      <span>1kg</span>
                    </div>
                  </div>

                  {hasLactoseOption ? (
                    <div className={`rounded-lg border p-3 ${hasImage ? 'border-white/25 bg-transparent shadow-none' : 'border-border/50 bg-background/70 shadow-lg'}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className={`text-sm font-medium ${hasImage ? 'text-white' : 'text-foreground'}`}>
                            Senza lattosio
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() => setSenzaLattosio((prev) => !prev)}
                          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                            senzaLattosio ? 'bg-green-500' : hasImage ? 'bg-white/30' : 'bg-muted'
                          }`}
                          aria-pressed={senzaLattosio}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                              senzaLattosio ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  ) : null}

                  <div className="flex items-center justify-between pt-2">
                    <div>
                      <span className={`text-xs ${hasImage ? 'text-white/80' : 'text-muted-foreground'}`}>Totale:</span>
                      <p className={`text-2xl font-bold ${hasImage ? 'text-white drop-shadow-lg' : 'gradient-instagram-text'}`}>
                        {formatPrice(calculatedPrice)}
                      </p>
                    </div>

                    <Button
                      onClick={handleAddToCart}
                      className={`${hasImage ? 'bg-white text-gray-900 hover:bg-white/90' : 'gradient-instagram text-primary-foreground hover:opacity-90'} shadow-instagram`}
                      data-testid={`add-to-cart-${dish.id}`}
                    >
                      <ShoppingCart className="mr-2 h-4 w-4" />
                      Aggiungi
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : !canShowPrice && dish.price_value != null ? (
            <div className={`border-t pt-2 ${hasImage ? 'border-white/20' : 'border-border/50'}`}>
              {hasLactoseOption ? (
                <div className={`mb-3 rounded-lg border p-3 ${hasImage ? 'border-white/25 bg-transparent shadow-none' : 'border-border/50 bg-background/70 shadow-lg'}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className={`text-sm font-medium ${hasImage ? 'text-white' : 'text-foreground'}`}>
                        Senza lattosio
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setSenzaLattosio((prev) => !prev)}
                      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                        senzaLattosio ? 'bg-green-500' : hasImage ? 'bg-white/30' : 'bg-muted'
                      }`}
                      aria-pressed={senzaLattosio}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                          senzaLattosio ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              ) : null}

              <Button
                onClick={handleAddToCart}
                className={`w-full ${hasImage ? 'bg-white text-gray-900 hover:bg-white/90' : 'gradient-instagram text-primary-foreground hover:opacity-90'} shadow-instagram`}
                data-testid={`add-to-cart-${dish.id}`}
              >
                <ShoppingCart className="mr-2 h-4 w-4" />
                Ordina Ora
              </Button>
            </div>
          ) : (
            <div className={`border-t pt-2 ${hasImage ? 'border-white/20' : 'border-border/50'}`}>
              <p className={`text-sm italic ${hasImage ? 'text-white/70' : 'text-muted-foreground'}`}>
                Prezzo non disponibile
              </p>
              <Button disabled variant="outline" className="mt-2 w-full">
                Non ordinabile
              </Button>
            </div>
          )}
        </div>
      </Card>

      <DishImagePreviewDialog
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        imageUrl={imageUrl}
        title={dish.name}
        categoryName={categoryName}
      />
    </>
  );
};

const MenuNavigationButtons = ({ className = '' }) => {
  const navigate = useNavigate();

  const goToMenuCompleto = () => {
    navigate('/menu-completo');
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 50);
  };

  const goToBevande = () => {
    navigate('/menu-completo#bevande');
  };

  return (
    <div className={className}>
      <div className="relative min-h-[320px] overflow-hidden rounded-[32px] border border-white/15 shadow-2xl sm:min-h-[380px] lg:min-h-[440px]">
        <div
          className="absolute inset-0 scale-110 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('/images/menu-buttons-bg.jpg')`,
          }}
        />

        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/30 to-black/60" />
        <div className="absolute inset-0 bg-gradient-to-br from-instagram-purple/20 via-transparent to-instagram-orange/20" />
        <div className="absolute inset-0 backdrop-blur-[1.5px]" />

        <div className="relative z-10 flex min-h-[320px] items-center justify-center px-6 py-10 sm:min-h-[380px] sm:px-10 sm:py-12 lg:min-h-[440px] lg:px-16">
          <div className="w-full max-w-4xl text-center">
            <span className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/90 backdrop-blur-md sm:text-xs">
              Scopri di piu
            </span>

            <h3 className="mt-4 text-2xl font-bold leading-tight text-white drop-shadow-xl sm:text-3xl lg:text-5xl">
              Esplora tutto il nostro menu
            </h3>

            <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-white/85 sm:text-base lg:text-lg">
              Bevande e specialita da aggiungere al tuo ordine
            </p>

            <div className="mt-7 flex flex-col justify-center gap-4 sm:flex-row">
              <Button
                type="button"
                onClick={goToMenuCompleto}
                className="h-12 rounded-2xl border border-white/25 bg-white/12 px-7 text-sm font-semibold text-white shadow-lg backdrop-blur-md transition-all duration-300 hover:bg-white/20 hover:border-white/35 sm:h-14 sm:px-8 sm:text-base"
              >
                <ChevronRight className="mr-2 h-4 w-4" />
                Scopri il menu completo
              </Button>

              <Button
                type="button"
                onClick={goToBevande}
                className="h-12 rounded-2xl border-0 bg-gradient-to-r from-instagram-purple via-instagram-red to-instagram-orange px-7 text-sm font-semibold text-white shadow-xl transition-all duration-300 hover:opacity-95 sm:h-14 sm:px-8 sm:text-base"
              >
                <Wine className="mr-2 h-4 w-4" />
                Aggiungi bevande
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const DailyMenuSection = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchVisibleMenu = async () => {
      try {
        const response = await fetch(`${APP_BASE}/api/public/menu/full/index.php`, {
          cache: 'no-store',
        });
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.message || data?.detail || data?.error || 'Errore nel caricamento del menu');
        }

        setCategories(Array.isArray(data?.categories) ? data.categories : []);
        setError(null);
      } catch (err) {
        console.error('Error fetching visible full menu:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchVisibleMenu();
  }, []);

  const categoryIcons = {
    antipasti: { icon: ChefHat, color: 'text-instagram-purple' },
    primi: { icon: ChefHat, color: 'text-instagram-red' },
    secondi: { icon: ChefHat, color: 'text-instagram-orange' },
    contorni: { icon: Leaf, color: 'text-success' },
  };

  if (loading) {
    return (
      <section className="py-10 px-4" id="menu-home">
        <div className="mx-auto max-w-6xl">
          <div className="flex h-64 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-instagram-purple"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="bg-muted/30 py-10 px-4" id="menu-home">
        <div className="mx-auto max-w-4xl text-center">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <h2 className="mb-4 text-2xl font-bold text-foreground sm:text-3xl">
            Menu non disponibile
          </h2>
          <p className="text-muted-foreground">Si prega di riprovare piu tardi.</p>
        </div>
      </section>
    );
  }

  if (categories.length === 0) {
    return (
      <section className="bg-muted/30 py-10 px-4" id="menu-home">
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="mb-4 text-2xl font-bold text-foreground sm:text-3xl">
              Nessun piatto <span className="gradient-instagram-text">disponibile oggi</span>
            </h2>
            <p className="mb-8 text-muted-foreground">
              Controlla il menu completo o torna piu tardi.
            </p>
          </div>

          <MenuNavigationButtons className="mb-8 mt-8" />
        </div>
      </section>
    );
  }

  const romeDateLabel = new Intl.DateTimeFormat('it-IT', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Rome',
  }).format(new Date());

  const sortedCategories = [...categories].sort(
    (a, b) => (a.sort_order ?? a.order ?? 0) - (b.sort_order ?? b.order ?? 0)
  );
  const visibleGroups = buildCategoryGroups(sortedCategories, ['antipasti', 'primi', 'secondi', 'contorni']).filter(
    (group) => Array.isArray(group.items) && group.items.length > 0
  );

  const handleOpenFullCategory = (groupKey) => {
    navigate(`/menu-completo?categoria=${groupKey}`);
    window.setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 120);
  };

  if (visibleGroups.length === 0) {
    return (
      <section className="bg-muted/30 py-10 px-4" id="menu-home">
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="mb-4 text-2xl font-bold text-foreground sm:text-3xl">
              Nessun piatto visibile nelle sezioni <span className="gradient-instagram-text">Antipasti, Primi, Secondi o Contorni</span>
            </h2>
            <p className="mb-8 text-muted-foreground">
              Controlla le categorie del menu completo oppure attiva i piatti del giorno dalla disponibilita settimanale.
            </p>
          </div>

          <MenuNavigationButtons className="mb-8 mt-8" />
        </div>
      </section>
    );
  }

  return (
    <section className="py-10 px-4" id="menu-home">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 text-center">
          <div className="mb-3 inline-flex items-center gap-2">
            <div className="h-2 w-2 animate-pulse rounded-full gradient-instagram" />
            <span className="text-xs font-semibold uppercase tracking-wider text-instagram-purple">
              Menu Completo
            </span>
            <div className="h-2 w-2 animate-pulse rounded-full gradient-instagram" />
          </div>

          <h2 className="mb-2 text-2xl font-bold text-foreground sm:text-3xl">
            Piatti disponibili <span className="gradient-instagram-text">oggi</span>
          </h2>

          <p className="text-sm text-muted-foreground">
            {romeDateLabel.charAt(0).toUpperCase() + romeDateLabel.slice(1)}. Seleziona la quantita e aggiungi al carrello.
          </p>
        </div>

        <div className="space-y-8">
          {visibleGroups.map((group) => {
            const categoryInfo = categoryIcons[group.key] || {
              icon: ChefHat,
              color: 'text-muted-foreground',
            };
            const IconComponent = categoryInfo.icon;
            const previewItems = group.items.slice(-3);

            return (
              <div key={group.key}>
                <div className="mb-4 flex items-center gap-3">
                  <div className={`rounded-lg border border-border bg-card p-2 ${categoryInfo.color}`}>
                    <IconComponent className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">{group.label}</h3>
                  <div className="h-px flex-1 bg-border" />
                </div>

                <div className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {previewItems.map((dish, index) => (
                    <div
                      key={dish.id}
                      className="h-full animate-slide-up"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <DishCard
                        dish={dish}
                        categoryName={dish.sourceCategoryName || group.label}
                        showPricesOnHome
                      />
                    </div>
                  ))}
                </div>

                <div className="mt-5 flex justify-center">
                  <Button
                    type="button"
                    className="min-w-[240px] rounded-2xl px-8 gradient-instagram text-primary-foreground shadow-instagram hover:opacity-90"
                    onClick={() => handleOpenFullCategory(group.key)}
                  >
                    <ChevronRight className="mr-2 h-4 w-4" />
                    {group.cta}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <MenuNavigationButtons className="mb-4 mt-10" />
      </div>
    </section>
  );
};
